import axios, { AxiosError, AxiosResponse, CancelToken } from 'axios';
import axiosRetry from 'axios-retry';
import { useMutation, UseMutationOptions } from 'react-query';
import type { DefaultUpdateOrCreateResponse } from '~/App/Api/rest';

export type UploadProgressHandler = (progress: number) => void;

export interface UploadToS3Options {
  onUploadProgress?: UploadProgressHandler;
  onUploadSuccess?: () => void;
  onUploadError?: () => void;
  currentProgress?: number;
  retries?: number;
  cancelToken?: CancelToken;
}

export default function uploadToS3(
  url: string,
  blob: Blob,
  type: string,
  options: UploadToS3Options = {}
) {
  const {
    onUploadProgress,
    retries = 5,
    currentProgress = 0,
    cancelToken,
  } = options;

  const uploadClient = axios.create();

  axiosRetry(uploadClient, {
    retries,
    retryDelay: axiosRetry.exponentialDelay,
  });

  return uploadClient({
    url,
    method: 'PUT',
    data: blob,
    headers: {
      'Content-Type': type,
    },
    onUploadProgress(evt) {
      const percentComplete = Math.floor((evt.loaded / blob.size) * 100);

      if (currentProgress < percentComplete)
        onUploadProgress?.(percentComplete);
    },
    cancelToken,
  });
}

export function uploadVideoToS3(
  url: string,
  blob: Blob,
  type: string = 'video/webm',
  options: UploadToS3Options = {}
) {
  return uploadToS3(url, blob, type, options);
}

interface UploadVideoMutationParams {
  url: string;
  video: Blob;
  type: string;
  options?: UploadToS3Options;
}

const uploadVideoMutation = ({
  url,
  video,
  type,
  options,
}: UploadVideoMutationParams) =>
  uploadVideoToS3(url, video, type, options).catch(() => {
    options?.onUploadError?.();
  });

export function useUploadVideoToS3(options?: UploadToS3Options) {
  const { mutateAsync: uploadVideoToS3Mutation } = useMutation(
    uploadVideoMutation,
    {
      onSuccess: (result, { options }: UploadVideoMutationParams) => {
        if (result && options?.onUploadSuccess) options?.onUploadSuccess();
      },
      onError: (_: Error, { options }: UploadVideoMutationParams) => {
        if (options?.onUploadError) options?.onUploadError();
      },
    }
  );

  return (url: string, video: Blob) =>
    uploadVideoToS3Mutation({
      url,
      video,
      type: video instanceof File ? video.type : 'video/webm',
      options,
    });
}

export function uploadGreetFailed(greetId: string) {
  const client = axios.create();

  axiosRetry(client, {
    retries: 5,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: () => true,
  });

  return client.post<DefaultUpdateOrCreateResponse<never>>(
    `/api/v2/greets/${greetId}/upload-failed`
  );
}

type UploadToS3Params = {
  url: string;
  file: File | Blob;
  fileType: string;
};

type UploadToS3Response = {
  method: string;
  url: string;
  assetUrl: string;
  expires: number;
};

export function useUploadImageToS3(
  options?: UseMutationOptions<
    AxiosResponse<UploadToS3Response>,
    AxiosError,
    UploadToS3Params
  >
) {
  return useMutation(
    ({ url, file, fileType }: UploadToS3Params) =>
      uploadToS3(url, file, fileType),
    options
  );
}
